/**
 * Security schemes for the three layers.
 *
 * OpenAPI cannot express the HMAC *computation*, so the signature/timestamp
 * headers are modelled as apiKey-in-header schemes and the real algorithm is
 * documented in `info` + per-endpoint x-codeSamples (Ruby / Node.js / cURL).
 */
export const securitySchemes = {
  /** Player API — the only credential the browser holds. */
  PlatformSession: {
    type: 'apiKey',
    in: 'header',
    name: 'X-Platform-Session',
    description:
      'Short-lived player session token (24h TTL) returned by `POST /v1/sessions`. ' +
      'Sent on every Player API request. It is *not* a cookie — requests must use ' +
      "fetch's default credentials mode (omit), never `credentials: 'include'`.",
  },
  /** Operator API — identifies the operator. */
  OperatorApiKey: {
    type: 'http',
    scheme: 'bearer',
    description:
      'Operator API key (`gp_…`). Sent as `Authorization: Bearer <api_key>`. ' +
      'Pairs with the HMAC signature headers below on every server-to-server call.',
  },
  /** HMAC headers — present on the Operator API *and* on the webhooks we send you. */
  RepackzTimestamp: {
    type: 'apiKey',
    in: 'header',
    name: 'X-Repackz-Timestamp',
    description: 'Current Unix time in **seconds**. Must be within ±5 minutes of server time.',
  },
  RepackzSignature: {
    type: 'apiKey',
    in: 'header',
    name: 'X-Repackz-Signature',
    description:
      '`sha256=` + lowercase-hex `HMAC_SHA256(api_secret, "<ts>.<METHOD> <request_uri>.<body>")`. ' +
      'See the Ruby / Node.js / cURL samples on any signed endpoint.',
  },
} as const;

/** Convenience requirement objects referenced by operations. */
export const SECURITY = {
  /** Player API. */
  player: [{ PlatformSession: [] }],
  /** Operator API (server-to-server) — all three headers required together. */
  operator: [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }],
  /** Public endpoints (e.g. just_pulled) — no auth. */
  none: [] as Array<Record<string, string[]>>,
} as const;
